"use client";

/**
 * Mantine Drawer Stack Component
 * 
 * Manages multiple drawers with proper z-index stacking, focus management,
 * and escape key handling. Follows Mantine best practices for drawer management.
 * 
 * IMPORTANT: Drawer.Stack can only be used with Drawer component, NOT with compound components
 * (Drawer.Root, Drawer.Content, etc.). Use this for simple Drawer components only.
 */

import * as React from "react";
import { Drawer, useDrawersStack } from '@mantine/core';

interface MantineDrawerStackProps {
  children: React.ReactNode;
  drawerIds: string[];
}

export const MantineDrawerStack: React.FC<MantineDrawerStackProps> = ({ children, drawerIds }) => {
  return (
    <Drawer.Stack>
      {children}
    </Drawer.Stack>
  );
};

// Note: Use Mantine's useDrawersStack directly instead of this wrapper
// export function useMantineDrawerStack(drawerIds: string[]) {
//   return useDrawersStack(drawerIds);
// }

// Legacy hook for URL-based drawer management (not compatible with Drawer.Stack)
export const useUrlDrawerStack = () => {
  const [drawers, setDrawers] = React.useState<Set<string>>(new Set());

  const openDrawer = React.useCallback((drawerId: string) => {
    setDrawers(prev => new Set([...prev, drawerId]));
  }, []);

  const closeDrawer = React.useCallback((drawerId: string) => {
    setDrawers(prev => {
      const newSet = new Set(prev);
      newSet.delete(drawerId);
      return newSet;
    });
  }, []);

  const closeAllDrawers = React.useCallback(() => {
    setDrawers(new Set());
  }, []);

  const isDrawerOpen = React.useCallback((drawerId: string) => {
    return drawers.has(drawerId);
  }, [drawers]);

  return {
    drawers: Array.from(drawers),
    openDrawer,
    closeDrawer,
    closeAllDrawers,
    isDrawerOpen,
    hasDrawers: drawers.size > 0
  };
};